import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowAltCircleLeft } from "@fortawesome/free-regular-svg-icons";
import {
  faCheck,
  faCheckCircle,
  faTimes,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";

import { AESDecrypt } from "../../../encrypt-util";
import UploadThing from "../../../components/common/UploadThing";
import { AppDispatch, RootState } from "../template/store";
import { updateDashbaordRefresh } from "../../common/components/DataTable/slices/DataTable";
import DataTable from "../../common/components/DataTable";
import {
  ApproveSezCertificate,
  getSezCertificateFieldDifferences,
  getSezCertificateRequest,
  getSezRejectedMessage,
  ISezCertificateFinalSubmitInvoice,
  ISezCertificateUploadInvoice,
  SezCertificateFieldDifferencesResult,
  submitSezCertificateFinal,
  uploadSezCertificate,
} from "./SezActions";
import SezInvoiceDetails, {
  InvoiceDetailsData,
  SezInvoiceEditableField,
  mapSezCertificateRequestToInvoiceDetails,
} from "./SezInvoiceDetails";

type DecisionType = "APPROVE" | "REJECT";

interface SezApproveRejectProps {
  onClose?: () => void;
}

interface UploadedFile {
  fileName: string;
  serverFileName: string;
  serverFilePath: string;
}

interface RelatedCertificateInvoice {
  sezInvNo: string;
  sezInvDt?: string;
  sezInvAmt?: string;
  sezInvTp?: string;
  sezOnlineReferenceNumber?: string;
}

interface CertificateUploadState {
  fileName: string;
  loading: number;
  uploadedFile: UploadedFile | null;
  uploading: boolean;
  uploaded: boolean;
  uploadedInvoices: ISezCertificateUploadInvoice[];
  relatedInvoices: RelatedCertificateInvoice[];
}

const initialCertificateUploadState: CertificateUploadState = {
  fileName: "",
  loading: 0,
  uploadedFile: null,
  uploading: false,
  uploaded: false,
  uploadedInvoices: [],
  relatedInvoices: [],
};

const toDateInputValue = (value?: string | null) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  }
  return "";
};

const getLinerRemarksValue = (item?: {
  status?: string | null;
  remarks?: string | null;
}) => {
  const status = String(item?.status || "").toUpperCase();
  return ["PENDING", "SUBMITTED"].includes(status)
    ? ""
    : String(item?.remarks || "");
};

const SezApproveReject = ({ onClose }: SezApproveRejectProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { sez_cert_req_id } = useParams();
  const profileDetails = useSelector((state: RootState) => state.userProfile.profile);

  const APP_PATH_PREFIX = process.env.REACT_APP_APP_PATH_PREFIX || "";

  let resolvedSezCertReqId = NaN;
  if (sez_cert_req_id) {
    try {
      resolvedSezCertReqId = Number(AESDecrypt(decodeURIComponent(sez_cert_req_id)));
    } catch {
      resolvedSezCertReqId = NaN;
    }
  }

  const [fetchedInvoiceDetails, setFetchedInvoiceDetails] =
    useState<InvoiceDetailsData | null>(null);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<DecisionType | null>(null);
  const [completedAction, setCompletedAction] = useState<DecisionType | null>(null);
  const [certState, setCertState] = useState<CertificateUploadState>(
    initialCertificateUploadState
  );

  const loginUser =
    profileDetails?.username ||
    profileDetails?.partnerName ||
    profileDetails?.email ||
    "";

  const refreshInvoiceDetails = async () => {
    if (!resolvedSezCertReqId || Number.isNaN(resolvedSezCertReqId)) {
      return;
    }

    try {
      const result = await dispatch(
        getSezCertificateRequest(resolvedSezCertReqId)
      ).unwrap();
      const first = result?.data?.[0];
      const mapped = mapSezCertificateRequestToInvoiceDetails(first);
      if (!mapped) {
        throw new Error("No invoice details found");
      }
      setFetchedInvoiceDetails(mapped);
      setRemarks(getLinerRemarksValue(first));
    } catch (e) {
      setFetchedInvoiceDetails(null);
      toast.error(getSezRejectedMessage(e, "Failed to fetch invoice details"));
    }
  };

  useEffect(() => {
    setRemarks("");
    setCompletedAction(null);
    setCertState(initialCertificateUploadState);

    if (!resolvedSezCertReqId || Number.isNaN(resolvedSezCertReqId)) {
      setFetchedInvoiceDetails(null);
      return;
    }

    let cancelled = false;
    const loadInvoiceDetails = async () => {
      try {
        const result = await dispatch(
          getSezCertificateRequest(resolvedSezCertReqId)
        ).unwrap();
        if (cancelled) return;
        const first = result?.data?.[0];
        const mapped = mapSezCertificateRequestToInvoiceDetails(first);
        if (!mapped) {
          throw new Error("No invoice details found");
        }
        setFetchedInvoiceDetails(mapped);
        setRemarks(getLinerRemarksValue(first));
      } catch (e) {
        if (!cancelled) {
          setFetchedInvoiceDetails(null);
          toast.error(getSezRejectedMessage(e, "Failed to fetch invoice details"));
        }
      }
    };

    loadInvoiceDetails();
    return () => {
      cancelled = true;
    };
  }, [dispatch, resolvedSezCertReqId]);

  const displayInvoiceDetails = fetchedInvoiceDetails || null;
  const normalizedRowStatus = String(fetchedInvoiceDetails?.status || "").toUpperCase();
  const isPending = normalizedRowStatus === "PENDING";
  const canReviewCertificate =
    Boolean(fetchedInvoiceDetails) && (!isPending || certState.uploaded);
  const showMismatchWarning = ["PENDING", "SUBMITTED"].includes(
    normalizedRowStatus
  );

  const canEditLinerRemarks =
    ["PENDING", "SUBMITTED"].includes(normalizedRowStatus) && !completedAction;
  const showDecisionActions =
    ["PENDING", "SUBMITTED"].includes(normalizedRowStatus) && !completedAction;
  const editableFields = useMemo<SezInvoiceEditableField[]>(
    () =>
      isPending &&
      !completedAction &&
      !submitting &&
      !certState.uploading
        ? ["requestId", "issueDate"]
        : [],
    [completedAction, isPending, submitting, certState.uploading]
  );
  const pendingDecisionFieldsIncomplete =
    isPending &&
    (!fetchedInvoiceDetails?.requestId?.trim() ||
      !fetchedInvoiceDetails?.issueDate?.trim());
  const [loadingDifferences, setLoadingDifferences] = useState(false);
  const [differences, setDifferences] =
    useState<SezCertificateFieldDifferencesResult | null>(null);

  const mismatchedRows = useMemo(
    () =>
      (differences?.comparison || [])
        .filter((item) => item.status !== "MATCHED")
        .map((item, index) => ({
          id: index + 1,
          fieldName: item.fieldName,
          invoiceValue: item.invoiceValue || "-",
          certificateValue: item.certificateValue || "-",
          status: item.status,
        })),
    [differences]
  );

  const hasMismatch = mismatchedRows.length > 0;

  const columnConfig = useMemo(
    () => [
      {
        accessor: "id",
        Header: "",
        width: 0,
        maxWidth: 0,
      },
      {
        accessor: "fieldName",
        Header: "Field",
        width: 180,
        maxWidth: 200,
      },
      {
        accessor: "invoiceValue",
        Header: "Invoice Data",
        width: 220,
        maxWidth: 240,
        Cell: (rowData: any) => (
          <span className="text-primary fw-semibold fs--1">
            {rowData?.row?.original?.invoiceValue || "-"}
          </span>
        ),
      },
      {
        accessor: "certificateValue",
        Header: "Certificate Data",
        width: 220,
        maxWidth: 240,
        Cell: (rowData: any) => (
          <span className="text-danger fw-semibold fs--1">
            {rowData?.row?.original?.certificateValue || "-"}
          </span>
        ),
      },
    ],
    []
  );

  const differencesLoadedRef = useRef(false);

  useEffect(() => {
    differencesLoadedRef.current = false;
    setDifferences(null);
    setLoadingDifferences(false);
  }, [resolvedSezCertReqId]);

  useEffect(() => {
    if (isPending && !certState.uploaded) {
      differencesLoadedRef.current = false;
      setDifferences(null);
      setLoadingDifferences(false);
    }
  }, [isPending, certState.uploaded]);

  const loadDifferences = useCallback(async () => {
    if (!resolvedSezCertReqId || Number.isNaN(resolvedSezCertReqId)) return;
    if (differencesLoadedRef.current) return;

    differencesLoadedRef.current = true;
    setLoadingDifferences(true);
    try {
      const result = await dispatch(
        getSezCertificateFieldDifferences(resolvedSezCertReqId)
      ).unwrap();
      setDifferences(result);
    } catch (e) {
      toast.error(
        getSezRejectedMessage(e, "Failed to fetch certificate differences")
      );
      setDifferences(null);
    } finally {
      setLoadingDifferences(false);
    }
  }, [dispatch, resolvedSezCertReqId]);

  useEffect(() => {
    if (canReviewCertificate) loadDifferences();
  }, [canReviewCertificate, loadDifferences]);

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(`${APP_PATH_PREFIX}/sezDashboard`);
    }
  };

  const clearFile = () =>
    setCertState((prev) => ({
      ...initialCertificateUploadState,
      uploading: prev.uploading,
    }));

  const applyCertUploadResponse = (
    result: {
      requestId?: string;
      issueDate?: string;
      invoices?: ISezCertificateUploadInvoice[];
    } | null
  ) => {
    const currentInvoiceNo = String(
      fetchedInvoiceDetails?.invoiceNo || ""
    )
      .trim()
      .toUpperCase();
    const invoices = result?.invoices || [];

    const relatedInvoices = invoices
      .map((inv) => {
        const invNo = String(inv?.invoiceNumber || "").trim();
        if (!invNo || invNo.toUpperCase() === currentInvoiceNo) return null;
        const status = String(inv?.status || "").trim().toUpperCase();
        if (status !== "PENDING" && status !== "REJECTED") return null;
        return {
          sezInvNo: invNo,
          sezInvDt:
            String(inv?.sezInvDt || inv?.invoiceDate || "").trim() || undefined,
          sezInvAmt:
            String(inv?.sezInvAmt || inv?.amountInr || "").trim() || undefined,
          sezInvTp:
            String(inv?.sezInvTp || inv?.invoiceType || "").trim() || undefined,
          sezOnlineReferenceNumber:
            String(inv?.sezOnlineReferenceNumber || "").trim() || undefined,
        };
      })
      .filter(Boolean)
      .filter(
        (inv, index, all) =>
          all.findIndex(
            (item) =>
              item!.sezInvNo.toUpperCase() === inv!.sezInvNo.toUpperCase()
          ) === index
      ) as RelatedCertificateInvoice[];

    setCertState((prev) => ({
      ...prev,
      uploadedInvoices: invoices,
      relatedInvoices,
    }));

    const matched = invoices.find(
      (inv) =>
        String(inv?.sezInvNo || inv?.invoiceNumber || "")
          .trim()
          .toUpperCase() === currentInvoiceNo
    );
    const uploadedRequestId = String(result?.requestId || "").trim();
    const uploadedIssueDate = toDateInputValue(
      matched?.issueDate ?? result?.issueDate
    );

    if (uploadedRequestId || uploadedIssueDate) {
      setFetchedInvoiceDetails((prev) => ({
        ...(prev || {}),
        ...(uploadedRequestId && { requestId: uploadedRequestId }),
        ...(uploadedIssueDate && { issueDate: uploadedIssueDate }),
      }));
    }
  };

  const handleCertificateUpload = async (
    name: string,
    serverPath: string,
    serverName: string
  ) => {
    if (!resolvedSezCertReqId || Number.isNaN(resolvedSezCertReqId)) {
      toast.error("SEZ Certificate Request ID is missing");
      return;
    }

    const file: UploadedFile = {
      fileName: name,
      serverFileName: serverName,
      serverFilePath: serverPath,
    };
    setCertState((prev) => ({
      ...prev,
      fileName: name,
      loading: 100,
      uploadedFile: file,
      uploading: true,
      uploaded: false,
      uploadedInvoices: [],
      relatedInvoices: [],
    }));

    try {
      const result = await dispatch(
        uploadSezCertificate({
          ...file,
          sezCertReqId: resolvedSezCertReqId,
          loginUser,
          locationId: 12,
        })
      ).unwrap();

      applyCertUploadResponse(result);
      await loadDifferences();
      await refreshInvoiceDetails();
      setCertState((prev) => ({ ...prev, uploaded: true }));
      toast.success(
        result?.message || "SEZ Certificate uploaded successfully",
        { style: { color: "white" } }
      );
      dispatch(updateDashbaordRefresh(true));
    } catch (e) {
      clearFile();
      toast.error(getSezRejectedMessage(e, "Failed to upload SEZ Certificate"));
    } finally {
      setCertState((prev) => ({ ...prev, uploading: false }));
    }
  };

  const submitPendingCertificateDetails = async () => {
    if (!fetchedInvoiceDetails) return;

    const {
      remarks: customerRemarks = "",
      requestId = "",
      issueDate = "",
      invoiceNo,
      invoiceDate,
      sezInvNo,
      status,
    } = fetchedInvoiceDetails;
    const currentInvoiceNo = String(invoiceNo || "").trim().toUpperCase();

    const invoicesPayload: ISezCertificateFinalSubmitInvoice[] =
      certState.uploadedInvoices.map((inv) => {
        const invNo = String(inv?.sezInvNo || inv?.invoiceNumber || "")
          .trim()
          .toUpperCase();
        return invNo === currentInvoiceNo
          ? {
              ...inv,
              remarks: customerRemarks.trim(),
              issueDate: issueDate.trim(),
            }
          : { ...inv };
      });

    const alreadyInPayload = invoicesPayload.some(
      (inv) =>
        String(inv?.sezInvNo || inv?.invoiceNumber || "")
          .trim()
          .toUpperCase() === currentInvoiceNo
    );
    if (currentInvoiceNo && !alreadyInPayload) {
      invoicesPayload.push({
        invoiceNumber: invoiceNo || null,
        invoiceDate: invoiceDate || null,
        issueDate: issueDate.trim(),
        remarks: customerRemarks.trim(),
        sezInvNo: sezInvNo || null,
      });
    }

    await dispatch(
      submitSezCertificateFinal({
        sezCertReqId: resolvedSezCertReqId,
        requestId: requestId.trim(),
        issueDate: issueDate.trim(),
        requestStatus: status || "",
        invoices: invoicesPayload,
      })
    ).unwrap();
  };

  const handleSubmit = async (action: DecisionType) => {
    if (action === "REJECT" && !remarks.trim()) {
      toast.error("Liner Remarks is mandatory for rejection");
      return;
    }

    if (!resolvedSezCertReqId || Number.isNaN(resolvedSezCertReqId)) {
      toast.error("SEZ Certificate Request ID is missing");
      return;
    }

    setSubmitting(true);
    setSubmittingAction(action);
    try {
      if (action === "APPROVE" && isPending) {
        await submitPendingCertificateDetails();
      }

      const result = await dispatch(
        ApproveSezCertificate({
          odexPartnerId: Number(profileDetails?.partnerId),
          sezCertReqId: resolvedSezCertReqId,
          status: action === "APPROVE" ? "APPROVED" : "REJECTED",
          email: String(profileDetails?.email),
          remarks: remarks.trim(),
          requestId: fetchedInvoiceDetails?.requestId?.trim() || "",
          issueDate: fetchedInvoiceDetails?.issueDate?.trim() || "",
        })
      ).unwrap();

      if (result?.status === "FAILED") {
        if (action === "APPROVE" && isPending) {
          clearFile();
          await refreshInvoiceDetails();
          dispatch(updateDashbaordRefresh(true));
        }
        toast.error(
          result?.message ||
            `Failed to ${action === "APPROVE" ? "approve" : "reject"} request`
        );
        return;
      }

      clearFile();
      await refreshInvoiceDetails();
      dispatch(updateDashbaordRefresh(true));
      toast.success(
        `Request ${action === "APPROVE" ? "approved" : "rejected"} successfully`
     ,  {   style:{
            color:"white"
          }} );
      setCompletedAction(action);
    } catch (e) {
      if (action === "APPROVE" && isPending) {
        await refreshInvoiceDetails();
        dispatch(updateDashbaordRefresh(true));
      }
      toast.error(
        getSezRejectedMessage(
          e,
          `Failed to ${action === "APPROVE" ? "approve" : "reject"} request`
        )
      );
    } finally {
      setSubmitting(false);
      setSubmittingAction(null);
    }
  };

  return (
    <Card className="h-100 border-0 overflow-auto sez-panel-scroll">
      <style>{`
        .sez-panel-scroll {
          scrollbar-width: thin;
          scrollbar-color: #d7dde7c7 transparent;
        }
        .sez-panel-scroll::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }
        .sez-panel-scroll::-webkit-scrollbar-thumb {
          background: #d7dde7c7;
          border-radius: 15px;
        }
        .sez-panel-scroll::-webkit-scrollbar-thumb:hover {
          background: #b7bcc4d8;
        }
        .sez-review-summary {
          gap: 0.75rem;
        }
        .sez-review-upload {
          border: 1px dashed #c7d2e0;
          border-radius: 0.75rem;
          background: #fafbfd;
        }
        .sez-review-upload-status {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          background: #e6f7ee;
          color: #1e8a5f;
          font-weight: 600;
          padding: 0.2rem 0.75rem;
        }
        .sez-diff-table-wrap {
          width: 100%;
          overflow-x: auto;
          scrollbar-width: thin;
          scrollbar-color: #d7dde7c7 transparent;
        }
        .sez-diff-table-wrap::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }
        .sez-diff-table-wrap::-webkit-scrollbar-thumb {
          background: #d7dde7c7;
          border-radius: 15px;
        }
        .sez-diff-table-wrap > * {
          min-width: 42rem;
        }
        @media (max-width: 575.98px) {
          .sez-review-header {
            align-items: flex-start !important;
          }
          .sez-review-counts {
            width: 100%;
          }
          .sez-review-actions .btn {
            width: 100%;
          }
          .sez-diff-table-wrap > * {
            min-width: 36rem;
          }
        }
      `}</style>
      <Card.Header className="d-flex align-items-center border-bottom sticky-top bg-white py-3 px-3 flex-wrap">
        <FontAwesomeIcon
          icon={faArrowAltCircleLeft}
          className="text-warning cursor-pointer fa-lg mb-1 me-2"
          onClick={handleBack}
        />
        <div>
          <h2 className="fs-0 text-primary mb-0 text-break">SEZ Certificate Review</h2>
        </div>
      </Card.Header>

      <Card.Body className="p-3 p-md-4 d-flex flex-column">
        {isPending && !completedAction && (
          <div className="d-flex flex-wrap align-items-center gap-3 sez-review-upload p-3 mb-4">
            <span className="fs--1 fw-semibold text-muted flex-shrink-0">
              Upload PDF file
            </span>
            <div className="flex-grow-1 min-w-0">
              <UploadThing
                name={certState.fileName}
                loading={certState.uploading ? 50 : certState.loading}
                fulfilled={handleCertificateUpload}
                failed={() => {
                  clearFile();
                  toast.error("Upload failed");
                }}
                pending={(progress: number) =>
                  setCertState((prev) => ({ ...prev, loading: progress }))
                }
                clear={clearFile}
                selected={(name: string) =>
                  setCertState((prev) => ({
                    ...prev,
                    fileName: name,
                    loading: 1,
                    uploadedFile: null,
                    uploaded: false,
                    uploadedInvoices: [],
                    relatedInvoices: [],
                  }))
                }
                fileLimit={10}
                fileType={["pdf"]}
                labelWidth="clamp(9rem, 28vw, 10rem)"
                disabled={submitting || certState.uploading}
              />
            </div>
            {certState.uploaded && !certState.uploading && (
              <span className="sez-review-upload-status fs--2 flex-shrink-0">
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                Certificate saved
              </span>
            )}
            {certState.uploading && (
              <span className="sez-review-upload-status fs--2 flex-shrink-0">
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                  style={{ width: "0.65rem", height: "0.65rem" }}
                />
                Reading certificate…
              </span>
            )}
            <small className="fs--2 text-muted ms-md-auto flex-shrink-0">
              PDF only · Max 10 MB
            </small>
          </div>
        )}

                <SezInvoiceDetails
                  invoiceForm={displayInvoiceDetails}
                  compact
                  editableFields={editableFields}
                  onFieldChange={(field: SezInvoiceEditableField, value: string) =>
                    setFetchedInvoiceDetails((prev) => ({
                      ...(prev || {}),
                      [field]: value,
                    }))
                  }
                />

        {isPending && certState.relatedInvoices.length > 0 && (
          <div className="mt-3 mb-3">
            <div className="fs--2 fw-semibold text-muted text-uppercase mb-2">
              Other invoice numbers in certificate
            </div>
            <div className="d-flex flex-wrap gap-2">
              {certState.relatedInvoices.map((inv) => (
                <span
                  key={inv.sezInvNo}
                  className="badge rounded-pill fs--2 px-3 py-2"
                  style={{
                    background: "#e8f1ff",
                    color: "#2c7be5",
                    fontWeight: 600,
                  }}
                  title={[
                    inv.sezInvDt && `Date: ${inv.sezInvDt}`,
                    inv.sezInvAmt && `Amount: ${inv.sezInvAmt}`,
                    inv.sezInvTp && `Type: ${inv.sezInvTp}`,
                    inv.sezOnlineReferenceNumber &&
                      `Ref: ${inv.sezOnlineReferenceNumber}`,
                  ]
                    .filter(Boolean)
                    .join(" | ")}
                >
                  {inv.sezInvNo}
                </span>
              ))}
            </div>
          </div>
        )}
        {canReviewCertificate && (
          <div className="mt-3 mb-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between sez-review-summary mb-2 sez-review-header">
              <div className="d-flex align-items-center min-w-0">
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="me-2"
                  style={{
                    color: loadingDifferences
                      ? "#6c757d"
                      : differences?.overallResult === "MATCH"
                      ? "#198754"
                      : "#f58020",
                  }}
                />
                <div className="fw-semibold fs--1">
                  {loadingDifferences ? (
                    <span className="text-muted d-inline-flex align-items-center">
                      Comparing invoice and certificate data...
                    </span>
                  ) : (
                    <>
                      <span className="text-dark">Status: </span>
                      <span
                        style={{
                          color:
                            differences?.overallResult === "MATCH"
                              ? "#198754"
                              : "#f58020",
                        }}
                      >
                        {differences?.overallResult || "-"}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {!loadingDifferences && differences && (
                <div className="fs--2 sez-review-counts">
                  <span className="text-success fw-semibold">
                    Matched: {differences.matchedFields ?? 0}
                  </span>
                  <span className="text-muted mx-1">|</span>
                  <span
                    className="fw-semibold"
                    style={{
                      color:
                        (differences.mismatchedFields ?? mismatchedRows.length) > 0
                          ? "#f58020"
                          : "#198754",
                    }}
                  >
                    Mismatched: {differences.mismatchedFields ?? mismatchedRows.length}
                  </span>
                </div>
              )}
            </div>

            {!loadingDifferences &&
              (differences?.blNo || differences?.invoiceNo || differences?.partnerName) && (
                <div className="fs--2 text-muted mb-2">
                  {[
                    differences?.blNo ? `BL: ${differences.blNo}` : null,
                    differences?.invoiceNo ? `Invoice: ${differences.invoiceNo}` : null,
                    differences?.partnerName || null,
                  ]
                    .filter(Boolean)
                    .join("  |  ")}
                </div>
              )}

            {hasMismatch ? (
              <>
                {showMismatchWarning && (
                  <div
                    className="fs--2 fw-semibold mb-2"
                    style={{ color: "#f58020" }}
                  >
                    The following fields in the SEZ certificate do not match the
                    invoice data on record. Please review before approving.
                  </div>
                )}
                <div className="sez-diff-table-wrap">
                  <DataTable
                    gridName="SEZ_CERT_DIFF"
                    columns={columnConfig}
                    data={mismatchedRows}
                    shortPrepend="sez_diff_"
                    perPage={100}
                    sortable={false}
                    searchable={false}
                    showColumnFilter={false}
                    selection={false}
                    pagination={false}
                    seekPagination={false}
                    showExport={false}
                    localFilter={false}
                    uniqueFilterKey="sezCertificateDifferences"
                    autoHeight={true}
                    maxHeight="40vh"
                    tableMinHeight={100}
                    staticHiddenCols={["id"]}
                  />
                </div>
                {showMismatchWarning && (
                  <div
                    className="fs--2 fw-semibold mt-2"
                    style={{ color: "#a85d12" }}
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="me-2"
                      style={{ color: "#f58020" }}
                    />
                    Approving will mark this certificate as Approved despite the
                    discrepancies. This action is logged for audit purposes.
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {displayInvoiceDetails && (
          <div className="mt-3 w-100">
            <div className="mb-3">
              <Form.Group controlId="sezCertificate.remarks">
                <Form.Label className="fs--1">
                  Liner Remarks
                </Form.Label>
                <Form.Control
                  className="fs--1 form-control"
                  as="textarea"
                  rows={3}
                  value={remarks}
                  maxLength={150}
                  disabled={!canEditLinerRemarks || submitting || loadingDifferences}
                  readOnly={!canEditLinerRemarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </Form.Group>
            </div>

       {showDecisionActions && (
       <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 sez-review-actions">
  <Button
    variant="success"
    className="approve"
    size="sm"
    disabled={
      submitting ||
      loadingDifferences ||
      certState.uploading ||
      (isPending && !certState.uploaded) ||
      pendingDecisionFieldsIncomplete
    }
    onClick={() => handleSubmit("APPROVE")}
  >
    <FontAwesomeIcon icon={faCheck} className="me-2 approve" />
    {submitting && submittingAction === "APPROVE" ? "Approving…" : t("resource_1:approve")}
  </Button>
  <Button
    variant="danger"
    className="reject"
    size="sm"
    disabled={
      submitting ||
      loadingDifferences ||
      certState.uploading ||
      pendingDecisionFieldsIncomplete
    }
    onClick={() => handleSubmit("REJECT")}
  >
    <FontAwesomeIcon icon={faTimes} className="me-2 reject" />
    {submitting && submittingAction === "REJECT" ? "Rejecting…" : t("resource_1:reject")}
  </Button>
</div>
       )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SezApproveReject;
